/* eslint-disable react/prop-types */
// noinspection JSCheckFunctionSignatures

import i18next from "i18next";

// Mapping of Arabic digits
const arabicDigits = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
const englishDigits = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
// String.fromCharCode(`0x066${d}`)
// English to Arabic digits using Unicode.
export const EnToArUni = (
  value,
  language = i18next.language,
  isReversed = false // true to reverse the conversion
) => {
  if (value === null || value === undefined) return value;

  return language === "ar"
    ? `${value}` &&
        `${value}`.replace(/\d/gm, (d) => String.fromCharCode(`0x066${d}`))
    : isReversed
    ? `${value}` &&
      `${value}`.replace(
        /[٠-٩]/gm,
        (d) => englishDigits[arabicDigits.indexOf(d)]
      )
    : value;
};

// Persian digits too, just in case (۰۱۲۳۴۵۶۷۸۹)
// Normalize any Arabic/Persian digits to ASCII 0–9
// const normalizeToAsciiDigits = (s) =>
//   s
//     // Arabic (٠١٢٣٤٥٦٧٨٩)
//     .replace(/[٠-٩]/g, (d) => String("0123456789"[arabicDigits.indexOf(d)]))
//     // Persian (۰۱۲۳۴۵۶۷۸۹)
//     .replace(/[۰-۹]/g, (d) => String("0123456789"[arabicDigits.indexOf(d)]));

const escapeRegex = (s) => s.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");

// Find locale group/decimal symbols via Intl
const getLocaleSeparators = (locale) => {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  const group = parts.find((p) => p.type === "group")?.value ?? ",";
  const decimal = parts.find((p) => p.type === "decimal")?.value ?? ".";

  return {group, decimal};
};

export const FormatNumbersWithDecimals = ({
  value,
  locale = "en-US",
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
  reverse = false, // optional: when true, unformats to a plain number
  roundOnReverse = false, // optional: round to `maximumFractionDigits` when reversing
}) => {
  if (value === null || value === undefined || value === "") return value;
  if (!reverse) {
    // ----- FORMAT -----
    const num =
      typeof value === "string"
        ? Number(value.replace(/,/g, ""))
        : Number(value);
    if (Number.isNaN(num)) return value;

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(num);
  }
  // ----- REVERSE (UNFORMAT) -----
  // 1) Coerce to string & normalize digits
  let str = String(value).trim();
  // str = normalizeToAsciiDigits(str);

  // Handle negative accounting format "(1,234.50)"
  let isNegative = false;
  if (str.startsWith("(") && str.endsWith(")")) {
    isNegative = true;
    str = str.slice(1, -1);
  }

  // 2) Identify locale separators
  const {group, decimal} = getLocaleSeparators(locale);

  // 3) Remove group separators (including NBSP and thin NBSP) and spaces
  const groupRegex = new RegExp(
    `[${escapeRegex(group)}\\u00A0\\u202F\\u2009\\s]`,
    "g"
  );
  str = str.replace(groupRegex, "");

  // 4) Convert locale decimal to '.'
  if (decimal !== ".") {
    const decRegex = new RegExp(escapeRegex(decimal), "g");
    str = str.replace(decRegex, ".");
  }

  // 5) Strip anything that’s not the digit, dot, or minus
  str = str.replace(/[^0-9.-]/g, "");

  // 6) Parse
  let num = Number(str);
  if (!Number.isFinite(num)) return null;

  if (isNegative) num = -Math.abs(num);

  // 7) Optional rounding (to match what the user sees)
  if (roundOnReverse) num = Number(num.toFixed(maximumFractionDigits));

  return num;
};
